import React, { useState, useEffect, useRef, useCallback } from 'react';
import SlideShell from '../SlideShell';

export default function NameInputSlide({
  title,
  subtitle = '',
  currentStep,
  totalSteps,
  onNext,
  onBack,
  initialValue = '',
  placeholder = "Enter your name",
  fieldName = 'name',
  minLength = 1,
  autoFocus = true,
  nextLabel = "Continue",
  animationDirection = 'forward',
  isTransitioning = false,
  onAutoSave = null,
}) {
  const [name, setName] = useState(initialValue || '');
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);
  const lastCommittedRef = useRef(JSON.stringify((initialValue || '').trim()))

  useEffect(() => {
    setName(initialValue || '');
    lastCommittedRef.current = JSON.stringify((initialValue || '').trim());
  }, [initialValue]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const commitAutoSave = useCallback((rawValue) => {
    if (!onAutoSave) return;
    const trimmed = (rawValue ?? '').trim();
    const serialized = JSON.stringify(trimmed);
    if (lastCommittedRef.current === serialized) return;
    lastCommittedRef.current = serialized;
    onAutoSave({ [fieldName]: trimmed });
  }, [onAutoSave, fieldName]);

  const isValid = (name?.trim().length || 0) >= minLength;

  const handleContinue = () => {
    if (!isValid) return;
    const trimmed = name.trim();
    commitAutoSave(trimmed);
    onNext({ [fieldName]: trimmed });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && isValid) {
      e.preventDefault();
      handleContinue();
    }
  };

  return (
    <SlideShell
      title={title}
      subtitle={subtitle}
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={handleContinue}
      onBack={onBack}
      nextLabel={nextLabel}
      disabled={!isValid}
      animationDirection={animationDirection}
      isTransitioning={isTransitioning}
      freezeViewportVars
    >
      <div className="w-full mx-auto">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
          Your name
        </label>
        <input
          id="name"
          ref={inputRef}
          type="text"
          inputMode="text"
          autoComplete="name"
          placeholder={placeholder}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            commitAutoSave(e.target.value);
          }}
          onBlur={() => {
            setTouched(true);
            commitAutoSave(name);
          }}
          onKeyDown={onKeyDown}
          className={`
            w-full rounded-xl border px-4 py-3 text-lg
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            ${!touched || isValid ? 'border-gray-300' : 'border-red-400'}
          `}
        />

        {!isValid && touched && (
          <p className="mt-2 text-sm text-red-500">
            Please enter your name.
          </p>
        )}
      </div>
    </SlideShell>
  );
}
